import BigNumber from 'bignumber.js';

// ---------------------------------------------------------------------------
// Multiple take-profits (partial exits / scale-out) — market-agnostic math.
//
// The user defines each exit by an allocation and a NET (optimized) R-multiple
// — e.g. "close 50% at a net 1.5R, 30% at 2.5R, 20% at 3.5R". Because the
// R-multiple is already net (fees included, the optimized take), the money
// outputs are exact for every market with no per-market fee math:
//
//     net_i      = w_i · r_i · riskAmount        (w_i = fraction of position)
//     R_i        = w_i · r_i
//     blended    = Σ w_i · r_i
//     totalNet$  = blended · riskAmount
//
// The DISPLAYED target price per exit reuses values the engine already returns,
// being affine in net-R through two known anchors — break-even (net R = 0) and
// the optimized take (net R = nominalRR):
//
//     T(r) = breakEven + (optimizedTakeProfit − breakEven) · (r / nominalRR)
//
// Exact for crypto/futures (linear fees); a close approximation for
// stocks/forex (display only — the R/$ outputs stay exact regardless).
//
// "Locked" = cumulative R secured by scaling out through that TP (rest still
// runs). "↩ Stop" = net R if, after those partials, price returns to the stop
// on the remainder: ifStopR = lockedR − remainingFraction.
// ---------------------------------------------------------------------------

const BN0 = new BigNumber(0);
const BN1 = new BigNumber(1);

const toBN = (v) => {
  if (v instanceof BigNumber) return v;
  if (v == null || v === '--' || v === '') return new BigNumber(NaN);
  try {
    const bn = new BigNumber(v);
    return bn.isNaN() ? new BigNumber(NaN) : bn;
  } catch {
    return new BigNumber(NaN);
  }
};

/**
 * @param {Object}   p
 * @param {Array}    p.exits   [{ alloc, rMultiple }] — alloc is % (mode 'percent') or a quantity (mode 'qty')
 * @param {string}   p.mode    'percent' | 'qty'
 * @param {BigNumber|string} p.quantity   total position size Q (needed for 'qty' mode)
 * @param {BigNumber|string} p.riskAmount net risk in $ (effectiveStop) — the 1R amount
 * @param {BigNumber|string} p.breakEven  break-even price (net R = 0 anchor)
 * @param {BigNumber|string} p.optimizedTakeProfit optimized TP price (net R = nominalRR anchor)
 * @param {BigNumber|string} p.nominalRR  the net R achieved at optimizedTakeProfit (price anchor)
 * @param {'long'|'short'}   p.side
 * @param {number}           [p.priceDecimals] round displayed target/avg prices to this many decimals
 * @param {BigNumber|string} [p.entryPrice]    entry price (for the gross-R distance per exit)
 * @param {BigNumber|string} [p.stopDistance]  |entry − stop| (for the gross-R distance per exit)
 * @param {BigNumber|string} [p.tpPathFee]     full take-profit-path fee ($, entry+exit) — per-exit fee = fraction · this (market-correct, never negative)
 * @returns {{ rows: Array, blendedNetRR: BigNumber, totalNetProfit: BigNumber,
 *            avgExitPrice: BigNumber, filled: BigNumber, runner: BigNumber }}
 */
export function computeMultiExits({
  exits,
  mode = 'percent',
  quantity,
  riskAmount,
  breakEven,
  optimizedTakeProfit,
  nominalRR,
  side = 'long',
  priceDecimals,
  entryPrice,
  stopDistance,
  tpPathFee,
}) {
  const Q = toBN(quantity);
  const risk = toBN(riskAmount);
  const be = toBN(breakEven);
  const optTp = toBN(optimizedTakeProfit);
  const anchorR = toBN(nominalRR);
  const entry = toBN(entryPrice);
  const dStop = toBN(stopDistance);
  const tpFeeFull = toBN(tpPathFee); // full take-profit-path fee in $ (entry + exit)

  const riskValid = risk.isFinite() && risk.gt(0);
  const priceValid =
    be.isFinite() && optTp.isFinite() && anchorR.isFinite() && anchorR.gt(0);
  // signed price move per 1 net-R (positive for long, negative for short)
  const pricePerR = priceValid ? optTp.minus(be).dividedBy(anchorR) : BN0;
  // Round displayed prices to the instrument's precision so the affine target
  // never surfaces long, meaningless decimals (fallback 8 if unknown).
  const pd = Number.isFinite(priceDecimals) ? Math.max(0, Math.min(12, priceDecimals)) : 8;
  const roundPrice = (b) => (b && b.isFinite && b.isFinite()) ? b.decimalPlaces(pd, BigNumber.ROUND_HALF_UP) : b;

  const list = Array.isArray(exits) ? exits : [];
  const rows = [];
  let cumR = BN0;
  let cumFrac = BN0;

  for (const ex of list) {
    const r = toBN(ex?.rMultiple);
    let w; // fraction of the position closed at this exit
    if (mode === 'qty') {
      const q = toBN(ex?.alloc);
      w = Q.isFinite() && Q.gt(0) && q.isFinite() ? q.dividedBy(Q) : BN0;
    } else {
      const pct = toBN(ex?.alloc);
      w = pct.isFinite() ? pct.dividedBy(100) : BN0;
    }

    const targetPrice =
      priceValid && r.isFinite() ? roundPrice(be.plus(pricePerR.multipliedBy(r))) : new BigNumber(NaN);
    // Gross ("dirty") R the price actually travels to reach this optimized target
    // (> the net R the user asked for, because fees push the target further out).
    const distR = (dStop.isFinite() && dStop.gt(0) && entry.isFinite() && targetPrice.isFinite())
      ? targetPrice.minus(entry).abs().dividedBy(dStop) : new BigNumber(NaN);
    const netR = r.isFinite() ? w.multipliedBy(r) : BN0; // R contribution of this exit
    const net = riskValid ? netR.multipliedBy(risk) : new BigNumber(NaN); // $ contribution
    const qty = (Q.isFinite() && Q.gt(0)) ? w.multipliedBy(Q) : new BigNumber(NaN); // units closed here
    // Per-exit fee = this portion's share of the full take-profit-path fee (entry +
    // exit), in market-correct $. Proportional by fraction → always ≥ 0, and Σ ≈ the
    // single-TP "Take Profit" fee. (gross = |T−entry|·qty was WRONG for futures/forex
    // where price×qty ≠ $ — pointValue / pip conversion — which produced the bogus
    // negative / huge fees.)
    const fee = tpFeeFull.isFinite() ? w.multipliedBy(tpFeeFull) : new BigNumber(NaN);

    cumR = cumR.plus(netR);
    cumFrac = cumFrac.plus(w);
    const rem = BigNumber.maximum(BN1.minus(cumFrac), BN0); // remaining open fraction (clamped)
    const ifStopR = cumR.minus(rem); // net R if the remainder then stops out

    rows.push({
      alloc: ex?.alloc,
      rMultiple: r,
      fraction: w,
      qty,
      targetPrice,
      distR,
      netR,
      net,
      fee,
      lockedR: cumR,
      lockedMoney: riskValid ? cumR.multipliedBy(risk) : new BigNumber(NaN),
      rem,
      ifStopR,
      ifStopMoney: riskValid ? ifStopR.multipliedBy(risk) : new BigNumber(NaN),
    });
  }

  const blendedNetRR = cumR; // Σ w·r
  const totalNetProfit = riskValid ? blendedNetRR.multipliedBy(risk) : new BigNumber(NaN);
  // Total fee if the whole position is scaled out via the TPs (entry + every exit).
  const totalFee = rows.reduce((a, x) => a.plus(x.fee && x.fee.isFinite && x.fee.isFinite() ? x.fee : BN0), BN0);
  const filled = cumFrac;
  const runner = BigNumber.maximum(BN1.minus(filled), BN0);

  const sumW = rows.reduce((a, x) => a.plus(x.fraction), BN0);
  const avgExitPrice =
    priceValid && sumW.gt(0)
      ? roundPrice(rows
          .reduce((a, x) => a.plus(x.fraction.multipliedBy(x.targetPrice)), BN0)
          .dividedBy(sumW))
      : new BigNumber(NaN);

  return { rows, blendedNetRR, totalNetProfit, totalFee, avgExitPrice, filled, runner, side };
}

// Default exit ladder used when the feature is first enabled (50/30/20 at 1.5/2.5/3.5R).
export const defaultTpExits = () => [
  { alloc: new BigNumber(50), rMultiple: new BigNumber(1.5) },
  { alloc: new BigNumber(30), rMultiple: new BigNumber(2.5) },
  { alloc: new BigNumber(20), rMultiple: new BigNumber(3.5) },
];

// Even allocation + evenly-stepped R-multiples for an arbitrary exit count (2–5).
export const buildTpExits = (count) => {
  const n = Math.max(2, Math.min(5, Number(count) || 3));
  const baseAlloc = Math.floor(100 / n);
  const out = [];
  let allocAcc = 0;
  for (let i = 0; i < n; i++) {
    const alloc = i === n - 1 ? 100 - allocAcc : baseAlloc;
    allocAcc += alloc;
    out.push({
      alloc: new BigNumber(alloc),
      rMultiple: new BigNumber(1.5 + i), // 1.5R, 2.5R, 3.5R, ...
    });
  }
  return out;
};
